/** Các enum nghiệp vụ dùng chung (trùng khớp với enum Prisma) và nhãn tiếng Việt. */

export const CATEGORY_KINDS = ['SOLAR_PANEL', 'INVERTER', 'BATTERY', 'OTHER'] as const;
export type CategoryKind = (typeof CATEGORY_KINDS)[number];
export const CATEGORY_KIND_LABELS: Record<CategoryKind, string> = {
  SOLAR_PANEL: 'Tấm pin',
  INVERTER: 'Biến tần',
  BATTERY: 'Pin lưu trữ',
  OTHER: 'Khác',
};

export const PRODUCT_STATUSES = ['ACTIVE', 'DISCONTINUED'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];
export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  ACTIVE: 'Đang kinh doanh',
  DISCONTINUED: 'Ngừng kinh doanh',
};

/** Loại hệ thống mặc định (danh sách thực tế quản lý trong bảng PackageOption) */
export const SYSTEM_TYPES = ['ONGRID', 'HYBRID', 'OFFGRID', 'OTHER'] as const;
export type SystemType = (typeof SYSTEM_TYPES)[number];
export const SYSTEM_TYPE_LABELS: Record<SystemType, string> = {
  ONGRID: 'Hòa lưới (On-grid)',
  HYBRID: 'Hybrid có lưu trữ',
  OFFGRID: 'Độc lập (Off-grid)',
  OTHER: 'Khác',
};

export const PACKAGE_STATUSES = ['DRAFT', 'ACTIVE', 'DISCONTINUED'] as const;
export type PackageStatus = (typeof PACKAGE_STATUSES)[number];
export const PACKAGE_STATUS_LABELS: Record<PackageStatus, string> = {
  DRAFT: 'Bản nháp',
  ACTIVE: 'Đang hoạt động',
  DISCONTINUED: 'Ngừng kinh doanh',
};

/** STANDARD = gói tiêu chuẩn; CUSTOM = gói tùy chỉnh do người lập báo giá tạo (giá chào nhập tay) */
export const PACKAGE_KINDS = ['STANDARD', 'CUSTOM'] as const;
export type PackageKind = (typeof PACKAGE_KINDS)[number];
export const PACKAGE_KIND_LABELS: Record<PackageKind, string> = {
  STANDARD: 'Gói tiêu chuẩn',
  CUSTOM: 'Gói tùy chỉnh',
};

export const VERSION_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const;
export type VersionStatus = (typeof VERSION_STATUSES)[number];
export const VERSION_STATUS_LABELS: Record<VersionStatus, string> = {
  DRAFT: 'Bản nháp',
  PUBLISHED: 'Đã phát hành',
  ARCHIVED: 'Đã lưu trữ',
};

export const PRICING_METHODS = ['FIXED', 'ITEMIZED', 'PER_KWP'] as const;
export type PricingMethod = (typeof PRICING_METHODS)[number];
export const PRICING_METHOD_LABELS: Record<PricingMethod, string> = {
  FIXED: 'Giá gói cố định',
  ITEMIZED: 'Tính theo danh mục vật tư',
  PER_KWP: 'Đơn giá theo kWp',
};

/** Cách tính chênh lệch giá khi thay đổi số tấm pin (áp dụng cho giá cố định và đơn giá theo kWp). */
export const PANEL_PRICE_MODES = ['NONE', 'PRODUCT_PRICE', 'CUSTOM_UNIT_PRICE'] as const;
export type PanelPriceMode = (typeof PANEL_PRICE_MODES)[number];
export const PANEL_PRICE_MODE_LABELS: Record<PanelPriceMode, string> = {
  // Chế độ cũ — nay xử lý như CUSTOM_UNIT_PRICE (dùng giá mỗi tấm trong Cài đặt), không còn cho chọn
  NONE: 'Cộng/trừ theo giá mỗi tấm trong Cài đặt',
  PRODUCT_PRICE: 'Cộng/trừ theo đơn giá tấm pin',
  CUSTOM_UNIT_PRICE: 'Cộng/trừ theo giá mỗi tấm khi tăng / khi giảm',
};

/** Các chế độ còn cho chọn khi cấu hình gói ("NONE" cũ được coi như CUSTOM_UNIT_PRICE). */
export const SELECTABLE_PANEL_PRICE_MODES = ['PRODUCT_PRICE', 'CUSTOM_UNIT_PRICE'] as const satisfies readonly PanelPriceMode[];
/** Chế độ thực tế khi tính giá: "NONE" (cũ) ⇒ cộng/trừ theo giá mỗi tấm (gói để trống ⇒ giá trong Cài đặt). */
export const effectivePanelPriceMode = (m: PanelPriceMode): Exclude<PanelPriceMode, 'NONE'> =>
  m === 'NONE' ? 'CUSTOM_UNIT_PRICE' : m;

export const ITEM_ROLES = ['PANEL', 'INVERTER', 'BATTERY', 'OTHER'] as const;
export type ItemRole = (typeof ITEM_ROLES)[number];
export const ITEM_ROLE_LABELS: Record<ItemRole, string> = {
  PANEL: 'Tấm pin chính',
  INVERTER: 'Biến tần chính',
  BATTERY: 'Pin lưu trữ',
  OTHER: 'Vật tư khác',
};

export const LOAN_METHODS = ['DECLINING_BALANCE', 'ANNUITY'] as const;
export type LoanMethod = (typeof LOAN_METHODS)[number];
export const LOAN_METHOD_LABELS: Record<LoanMethod, string> = {
  DECLINING_BALANCE: 'Dư nợ giảm dần (gốc đều, lãi trên dư nợ còn lại)',
  ANNUITY: 'Thanh toán đều hàng tháng (niên kim)',
};

export const RATE_CONVERSIONS = ['NOMINAL_DIVIDE_12', 'EFFECTIVE_ANNUAL'] as const;
export type RateConversion = (typeof RATE_CONVERSIONS)[number];
export const RATE_CONVERSION_LABELS: Record<RateConversion, string> = {
  NOMINAL_DIVIDE_12: 'Lãi suất danh nghĩa năm ÷ 12',
  EFFECTIVE_ANNUAL: 'Lãi suất thực năm, quy đổi kép (1+r)^(1/12) − 1',
};

export const QUOTE_STATUSES = [
  'DRAFT',
  'ISSUED',
  'SENT',
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
  'CANCELLED',
] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  DRAFT: 'Bản nháp',
  ISSUED: 'Đã phát hành',
  SENT: 'Đã gửi',
  ACCEPTED: 'Đã chấp nhận',
  REJECTED: 'Đã từ chối',
  EXPIRED: 'Hết hạn',
  CANCELLED: 'Đã hủy',
};

/** Các chuyển trạng thái thủ công được phép (phát hành đi qua endpoint riêng). */
export const QUOTE_STATUS_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  DRAFT: ['CANCELLED'],
  ISSUED: ['SENT', 'ACCEPTED', 'REJECTED', 'CANCELLED'],
  SENT: ['ACCEPTED', 'REJECTED', 'CANCELLED'],
  ACCEPTED: ['CANCELLED'],
  REJECTED: [],
  EXPIRED: ['CANCELLED'],
  CANCELLED: [],
};

export const QUOTE_VERSION_STATUSES = ['DRAFT', 'ISSUED', 'SUPERSEDED'] as const;
export type QuoteVersionStatus = (typeof QUOTE_VERSION_STATUSES)[number];
export const QUOTE_VERSION_STATUS_LABELS: Record<QuoteVersionStatus, string> = {
  DRAFT: 'Bản nháp',
  ISSUED: 'Đã phát hành',
  SUPERSEDED: 'Đã thay thế',
};

export const CUSTOMER_TYPES = ['HOUSEHOLD', 'BUSINESS', 'ENTERPRISE', 'OTHER'] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];
export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  HOUSEHOLD: 'Hộ gia đình',
  BUSINESS: 'Hộ kinh doanh',
  ENTERPRISE: 'Doanh nghiệp',
  OTHER: 'Khác',
};

export const TEMPLATE_KINDS = ['COVER_LETTER', 'PROPOSAL', 'ITEMS', 'FINANCING', 'COMBINED'] as const;
export type TemplateKind = (typeof TEMPLATE_KINDS)[number];
export const TEMPLATE_KIND_LABELS: Record<TemplateKind, string> = {
  COVER_LETTER: 'Thư ngỏ',
  PROPOSAL: 'Bản chào giá',
  ITEMS: 'Danh mục vật tư thiết bị',
  FINANCING: 'Tính toán gói vay',
  COMBINED: 'Mẫu tổng hợp',
};

export const POST_STATUSES = ['DRAFT', 'PUBLISHED', 'HIDDEN'] as const;
export type PostStatus = (typeof POST_STATUSES)[number];
export const POST_STATUS_LABELS: Record<PostStatus, string> = {
  DRAFT: 'Bản nháp',
  PUBLISHED: 'Đã đăng',
  HIDDEN: 'Đã ẩn',
};

export const DISCOUNT_TYPES = ['PERCENT', 'AMOUNT'] as const;
export type DiscountType = (typeof DISCOUNT_TYPES)[number];

export const VIETNAM_PROVINCES = [
  'Hà Nội',
  'TP. Hồ Chí Minh',
  'Hải Phòng',
  'Đà Nẵng',
  'Cần Thơ',
  'Huế',
  'An Giang',
  'Bắc Ninh',
  'Cà Mau',
  'Cao Bằng',
  'Đắk Lắk',
  'Điện Biên',
  'Đồng Nai',
  'Đồng Tháp',
  'Gia Lai',
  'Hà Tĩnh',
  'Hưng Yên',
  'Khánh Hòa',
  'Lai Châu',
  'Lâm Đồng',
  'Lạng Sơn',
  'Lào Cai',
  'Nghệ An',
  'Ninh Bình',
  'Phú Thọ',
  'Quảng Ngãi',
  'Quảng Ninh',
  'Quảng Trị',
  'Sơn La',
  'Tây Ninh',
  'Thái Nguyên',
  'Thanh Hóa',
  'Tuyên Quang',
  'Vĩnh Long',
] as const;
